import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";

export const WHATSAPP_NUMBER = "919988771606";
export const WHATSAPP_DISPLAY = "+91 99887 71606";

/** Builds a wa.me deep link with a pre-filled message. */
export function waLink(message) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

const quickReplies = [
  "I need help with the app",
  "I want to register my shop",
  "Question about wallet & credits",
];

export default function FloatingWhatsApp() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("Hi Time2Cash! I'd like some help.");

  const send = () => {
    window.open(waLink(message || "Hi Time2Cash!"), "_blank");
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col items-start gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-testid="whatsapp-chat-card"
            className="w-[300px] sm:w-[340px] rounded-3xl overflow-hidden bg-white shadow-2xl border border-[#EBE8E3]"
          >
            {/* Header */}
            <div className="bg-[#075E54] text-white p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                <WhatsAppIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-sm">Time2Cash Support</div>
                <div className="text-[11px] text-white/80 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" /> Typically replies instantly
                </div>
              </div>
              <button data-testid="whatsapp-close" onClick={() => setOpen(false)} aria-label="Close chat" className="p-1 rounded-full hover:bg-white/10">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 bg-[#ECE5DD]">
              <div className="bg-white rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-[#1A1918] shadow-sm max-w-[85%]">
                Hi there! 👋 How can we help you today? Send us a message and we&apos;ll reply on WhatsApp.
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {quickReplies.map((q) => (
                  <button
                    key={q}
                    onClick={() => setMessage(q)}
                    className="text-[11px] bg-white border border-[#d9d2c8] rounded-full px-3 py-1.5 text-[#075E54] font-medium hover:bg-[#f4f0ea] transition"
                  >
                    {q}
                  </button>
                ))}
              </div>

              <div className="mt-3 flex items-end gap-2">
                <textarea
                  data-testid="whatsapp-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={2}
                  placeholder="Type your message…"
                  className="flex-1 resize-none rounded-2xl border border-[#d9d2c8] bg-white px-3 py-2 text-sm outline-none focus:border-[#25D366]"
                />
                <button
                  data-testid="whatsapp-send"
                  onClick={send}
                  aria-label="Send on WhatsApp"
                  className="w-11 h-11 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white flex items-center justify-center shadow-lg transition-colors flex-shrink-0"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <motion.button
        data-testid="whatsapp-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat on WhatsApp"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.4, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="relative w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-xl shadow-emerald-500/30"
      >
        {!open && <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-40" />}
        <WhatsAppIcon className="w-7 h-7 relative" />
      </motion.button>
    </div>
  );
}
